import { Alert } from '@/shared/ui';
import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface Props {
    inputsCount?: number;
    updateCode: (val: string) => void;
    errorMessage: string;
    code: string;
}

interface InputType extends Event {
    inputType: string;
}

const VerificationInputs = ({ inputsCount = 6, updateCode, errorMessage, code }: Props) => {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const [focusedItem, setFocusedItem] = useState<null | number>(null);
    const [values, setValues] = useState<string[]>(Array(inputsCount).fill(''));

    useEffect(() => {
        // if we get an empty code coming in, we should reset the values
        if (!code) {
            setValues(Array(inputsCount).fill(''));
        }
    }, [code]);


    useEffect(() => {
        if (!errorMessage) {
            setValues(Array(inputsCount).fill(''));
            updateCode('');
        }
    }, [errorMessage, inputsCount, updateCode]);

    const errorClasses = useMemo(() => {
        return errorMessage ? 'border-red-500 border-2 focus:border-red-700' : '';
    }, [errorMessage]);

    const pasteCopiedValue = useCallback(
        (value: string) => {
            const newValues = Array(inputsCount).fill('');
            const pasteValue = value.slice(0, inputsCount);

            for (let i = 0; i < pasteValue.length; i++) {
                newValues[i] = pasteValue[i];
            }

            inputRefs.current[pasteValue.length - 1]?.focus();
            setValues(newValues);
            updateCode(newValues.join(''));
        },
        [inputsCount, updateCode]
    );

    const handleInput = useCallback(
        (event: ChangeEvent<HTMLInputElement>, index: number) => {
            const inputType = (event.nativeEvent as InputType).inputType;
            const val = event.target.value;
            if (val === ' ' || inputType === 'deleteContentBackward') return;
            const newValues = [...values];

            const isNumeric = new RegExp('^[0-9]*$').test(val);

            if (!isNumeric) return;

            if (inputType === 'insertFromPaste' || val.length === inputsCount) {
                pasteCopiedValue(val);
                return;
            }

            if (val.length >= 1) {
                newValues[index] = val[val.length - 1];
                if (index < inputRefs.current.length - 1) inputRefs.current[index + 1]?.focus();
            } else {
                newValues[index] = val;
            }

            setValues(newValues);
            updateCode(newValues.join(''));
        },
        [updateCode, values, pasteCopiedValue]
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>, index: number) => {
            if (event.key === 'Backspace' || event.key === 'Delete') {
                const newValues = [...values];
                let prevIndex = index - 1;
                while (prevIndex >= 0 && newValues[prevIndex] === '') {
                    prevIndex--;
                }
                if (newValues[index] === '') {
                    if (prevIndex >= 0) {
                        newValues[prevIndex] = '';
                        inputRefs.current[prevIndex]?.focus();
                    }
                } else {
                    newValues[index] = '';
                    inputRefs.current[prevIndex]?.focus();
                }
                setValues(newValues);
                updateCode(newValues.join(''));
            }
        },
        [focusedItem, updateCode, values]
    );


    useEffect(() => {
        if (focusedItem !== null) {
            if (values.some(val => val === '')) {
                const firstIndexWithEmpty = values.findIndex((item) => item === '');

                if (firstIndexWithEmpty !== -1) {
                    inputRefs.current[firstIndexWithEmpty]?.focus();
                }


            } else if (values.every(val => val !== '')) {
                inputRefs.current[values.length - 1]?.focus();
            }

        }
    }, [focusedItem, values]);

    return (
        <div className="flex flex-col gap-6 lg:gap-7" data-testid="verification-inputs-container">
            <div className="flex justify-between gap-2.5 md:gap-3" data-testid="verification-inputs-content">
                {values.map((e, i) => (
                    <input
                        key={`input${i}`}
                        onFocus={() => setFocusedItem(i)}
                        ref={el => (inputRefs.current[i] = el)}
                        value={e}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        className={`${errorClasses} verification_input-${i + 1} h-[55px] w-full max-w-[63px] rounded border border-black pl-2.5 text-22 outline-none focus:border-2 focus:border-primary focus:shadow-verifyInp sm:pl-[18px] md:pl-[22px] lg:h-[60px] lg:w-[63px] lg:text-26 xl:pl-6 ms:pl-4 xs:pl-[20px]`}
                        onInput={(event: ChangeEvent<HTMLInputElement>) => handleInput(event, i)}
                        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => handleKeyDown(event, i)}
                        data-testid={`verification-input-${i}`}
                    />
                ))}
            </div>

            {errorMessage && <Alert testId="verification-inputs-error-message" label={errorMessage} />}
        </div>
    );
};

export default VerificationInputs;
